import clsx from "clsx"
import React, { PropsWithChildren, ReactNode } from "react"
import { Box, Drawer, IconButton, Link, makeStyles, Typography } from "@material-ui/core"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowLeft } from "@fortawesome/pro-light-svg-icons/faArrowLeft"
import { faTimes } from "@fortawesome/pro-light-svg-icons/faTimes"
import { SafeArea } from "shared/Components/Skeleton/SafeArea"
import ErrorHandlerModal from "shared/Modules/Error/Components/ErrorHandlerModal"
import { LocalizedStrict } from "shared/Modules/Localization/Components/Localized"

// Same paper as the other bottom drawers in the app (DrawerWithSpacing / StandardDrawer)
const useStyles = makeStyles((theme) => ({
    drawerPaper: {
        top: theme.spacing(6),
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        overflowX: "hidden",
    },
    root: {
        display: "flex",
        flexDirection: "column",
        height: "100%",
    },
    header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: theme.spacing(1, 2, 0, 1),
        minHeight: theme.spacing(6),
    },
    closeIcon: {
        color: theme.palette.common.black,
    },
    progress: {
        display: "flex",
        gap: theme.spacing(1),
        padding: theme.spacing(0, 2),
    },
    segment: {
        flex: 1,
        height: 3,
        borderRadius: 2,
        backgroundColor: theme.palette.grey[300],
    },
    segmentDone: {
        backgroundColor: theme.palette.primary.main,
    },
    intro: {
        padding: theme.spacing(3, 2, 2),
    },
    stepLabel: {
        color: theme.palette.text.secondary,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        display: "block",
        marginBottom: theme.spacing(1),
    },
    title: {
        fontWeight: "bold",
        marginBottom: theme.spacing(1),
    },
    content: {
        flex: 1,
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
        // Room for the floating label of an outlined text field at the top (otherwise clipped by the scroll box)
        paddingTop: theme.spacing(1),
    },
    actions: {
        padding: theme.spacing(2),
        borderTop: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
    },
}))

type OnboardingDrawerShellProps = Readonly<PropsWithChildren<{
    open: boolean
    // Used by the error boundary to leave the flow
    onClose: () => void
}>>

/**
 * The bottom drawer itself. Mounted once by the onboarding flow and kept open across steps so the
 * steps swap their content in place instead of sliding a new drawer in for every step.
 */
export function OnboardingDrawerShell({ open, onClose, children }: OnboardingDrawerShellProps) {
    const classes = useStyles()

    return (
        <Drawer
            anchor="bottom"
            open={open}
            // The drawer can only be left through the steps' own actions (X / skip / buttons)
            aria-labelledby="onboarding-title"
            PaperProps={{ square: false }}
            classes={{ paper: classes.drawerPaper }}
        >
            <ErrorHandlerModal close={onClose}>
                <SafeArea bottom fullHeight method="padding">
                    {children}
                </SafeArea>
            </ErrorHandlerModal>
        </Drawer>
    )
}

type OnboardingDrawerProps = Readonly<PropsWithChildren<{
    name: string
    // Current step; omit to hide the progress bar and "Step X of Y" label (e.g. on the final screen)
    step?: number
    totalSteps: number
    title: ReactNode
    description?: ReactNode
    // Rendered above the title (e.g. an icon)
    media?: ReactNode
    // Shown as an X in the header; omit to make the step mandatory
    onClose?: () => void
    // Shown as a back arrow in the header (takes precedence over the X)
    onBack?: () => void
    // Shown as a "Skip" link in the header
    onSkip?: () => void
    // Sticky bottom area, typically the primary button
    actions?: ReactNode
}>>

/**
 * Content of one onboarding step inside OnboardingDrawerShell: progress bar, "Step X of Y" label,
 * title, description and an optional sticky action area.
 */
export function OnboardingDrawer({ name, step, totalSteps, title, description, media, onClose, onBack, onSkip, actions, children }: OnboardingDrawerProps) {
    const classes = useStyles()
    const segments = Array.from({ length: totalSteps }, (_, index) => index + 1)
    const showHeader = Boolean(onClose || onBack || onSkip)

    return (
        <Box className={classes.root} data-step={name}>
            {showHeader && (
                <Box className={classes.header}>
                    <Box>
                        {onBack ? (
                            <IconButton onClick={onBack} color="inherit" aria-label="back">
                                <FontAwesomeIcon icon={faArrowLeft} className={classes.closeIcon} />
                            </IconButton>
                        ) : onClose && (
                            <IconButton onClick={onClose} color="inherit" aria-label="close">
                                <FontAwesomeIcon icon={faTimes} className={classes.closeIcon} />
                            </IconButton>
                        )}
                    </Box>
                    {onSkip && (
                        <LocalizedStrict id="onboarding-skip-step">
                            <Link component="button" variant="body2" color="textSecondary" underline="none" onClick={onSkip}>
                                Spring over
                            </Link>
                        </LocalizedStrict>
                    )}
                </Box>
            )}
            {step !== undefined && (
                <Box className={classes.progress}>
                    {segments.map((segment) => (
                        <Box key={segment} className={clsx(classes.segment, { [classes.segmentDone]: segment <= step })} />
                    ))}
                </Box>
            )}
            <Box className={classes.intro}>
                {media}
                {step !== undefined && (
                    <LocalizedStrict id="onboarding-step-of" vars={{ step, total: totalSteps }}>
                        <Typography variant="caption" className={classes.stepLabel}>
                            Trin {step} af {totalSteps}
                        </Typography>
                    </LocalizedStrict>
                )}
                <Typography id="onboarding-title" variant="h5" className={classes.title}>
                    {title}
                </Typography>
                {description && (
                    <Typography variant="body2" color="textSecondary">
                        {description}
                    </Typography>
                )}
            </Box>
            <Box className={classes.content}>
                {children}
            </Box>
            {actions && (
                <Box className={classes.actions}>
                    {actions}
                </Box>
            )}
        </Box>
    )
}
